import {
	is1pProductKey,
	ProductKeys,
	ProductKeys3P,
	products1P,
} from '../../../common/constants/products';
import type { ThirdPartyConfigsWithAPIResponse } from '../../../common/constants/schemas/3p-config';
import {
	type GhostTextData,
	GhostTextExperimentCohort,
	ROTATION_THRESHOLD,
} from '../../../common/utils/quick-find/ghost-text';

import { ghostTextMessages } from './ghost-text-messages';

// TODO: to be used in second experiment https://hello.atlassian.net/wiki/pages/resumedraft.action?draftId=6192988799
/**
 * Generate ghost text rotations for SAIN cohort (AI-powered question suggestions)
 */
// const calculateSainGhostTextRotations = (
// 	formatMessage: (message: any, values?: any) => string,
// ): string[] => {
// 	return [
// 		formatMessage(ghostTextMessages.sainQuestionTimeOff),
// 		formatMessage(ghostTextMessages.sainQuestionResetPassword),
// 		formatMessage(ghostTextMessages.sainQuestionNotifications),
// 		formatMessage(ghostTextMessages.sainQuestionITSupport),
// 		formatMessage(ghostTextMessages.sainQuestionMeetingRoom),
// 		formatMessage(ghostTextMessages.sainQuestionBenefits),
// 	];
// };

/**
 * Generate ghost text rotations for 3P cohort (based on available products)
 * Returns ordered list: combined texts -> 3P specific texts (sorted by hash)
 */
const calculate3PGhostTextRotations = ({
	products,
	thirdPartyConfigs,
	primaryProduct,
	formatMessage,
}: {
	products: ProductKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
	primaryProduct?: string;
	formatMessage: (message: any, values?: any) => string;
}): string[] => {
	const SUPPORTED_3P_MESSAGES = new Map<string, any>([
		[ProductKeys3P.GoogleDrive, ghostTextMessages.googleDrivePlaceholder],
		[ProductKeys3P.Sharepoint, ghostTextMessages.sharepointPlaceholder],
		[ProductKeys3P.OneDrive, ghostTextMessages.onedrivePlaceholder],
		[ProductKeys3P.Teams, ghostTextMessages.teamsPlaceholder],
		[ProductKeys3P.Slack, ghostTextMessages.slackPlaceholder],
		[ProductKeys3P.Github, ghostTextMessages.githubPlaceholder],
	]);

	const currentAppName =
		primaryProduct && is1pProductKey(primaryProduct)
			? products1P[primaryProduct]?.displayName
			: undefined;

	const available1PExcludePrimary = Array.from(
		new Set(
			products
				.filter(is1pProductKey)
				.map((productKey) => {
					// Consolidate Jira variants to base Jira for ghost text
					if (productKey === ProductKeys.JiraProductDiscovery ||
						productKey === ProductKeys.JiraServiceManagement) {
						return ProductKeys.Jira;
					}
					return productKey;
				})
		)
	).filter((productKey) => productKey !== primaryProduct);

	const available3PProducts = Object.keys(thirdPartyConfigs || {}).filter(
		(key) =>
			thirdPartyConfigs?.[key] !== undefined &&
			!thirdPartyConfigs?.[key]?.userNeedsOAuth &&
			!key.toLowerCase().startsWith('forge-connector-') &&
			!key.toLowerCase().includes(ProductKeys3P.AtlassianWebCrawler) &&
			!key.toLowerCase().endsWith('-dc')
	);

	const textRotations: string[] = [];

	if (currentAppName) {
		if (available3PProducts.length >= 1 && available1PExcludePrimary.length >= 1) {
			const top1P = products1P[available1PExcludePrimary[0]].displayName;
			const top3P = thirdPartyConfigs?.[available3PProducts[0]]?.displayName;

			textRotations.push(
				formatMessage(ghostTextMessages.mixedAppsPlaceholder, {
					currentApp: currentAppName,
					otherFirstPartyApp: top1P,
					thirdPartyApp: top3P,
				}),
			);
		}

		if (available3PProducts.length >= 2) {
			const top3P1 = thirdPartyConfigs?.[available3PProducts[0]]?.displayName;
			const top3P2 = thirdPartyConfigs?.[available3PProducts[1]]?.displayName;

			textRotations.push(
				formatMessage(ghostTextMessages.multipleThirdPartyPlaceholder, {
					firstPartyApp: currentAppName,
					thirdPartyApp1: top3P1,
					thirdPartyApp2: top3P2,
				}),
			);
		}

		if (available3PProducts.length === 1 && available1PExcludePrimary.length === 0) {
			const top3P = thirdPartyConfigs?.[available3PProducts[0]]?.displayName;

			textRotations.push(
				formatMessage(ghostTextMessages.twoAppPlaceholder, {
					firstPartyApp: currentAppName,
					thirdPartyApp1: top3P,
				}),
			);
		}
	}

	for (const productKey of available3PProducts) {
		const message = SUPPORTED_3P_MESSAGES.get(productKey);
		if (message) {
			textRotations.push(formatMessage(message));
		}
	}

	return textRotations;
};

/**
 * Calculate all ghost text rotations based on experiment cohort.
 */
export const calculateGhostTextRotations = ({
	products,
	thirdPartyConfigs,
	primaryProduct,
	formatMessage,
}: {
	products: ProductKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
	primaryProduct?: string;
	formatMessage: (message: any, values?: any) => string;
}): string[] => {
	const textRotations: string[] = calculate3PGhostTextRotations({
		products,
		thirdPartyConfigs,
		primaryProduct,
		formatMessage,
	}).filter((text) => text.length <= 85);

	textRotations.push(formatMessage(ghostTextMessages.default3PPlaceholder));

	return textRotations;
};

/**
 * Select the appropriate ghost text based on occurrence count.
 * Each text is shown 3 times before rotating to the next.
 *
 * @param rotations - Ordered list of text rotations
 * @param currentText - The currently displayed text
 * @param occurrence - Number of times current text has been shown (0-2)
 * @returns Object with selected text and updated occurrence
 */
export const selectGhostText = (
	rotations: string[],
	currentText?: string,
	occurrence: number = 0,
): { text: string; occurrence: number } => {
	if (rotations.length === 0) {
		return { text: '', occurrence: 0 };
	}

	// Find current position by comparing text directly
	let currentIndex = 0;
	if (currentText) {
		const foundIndex = rotations.indexOf(currentText);
		if (foundIndex >= 0) {
			currentIndex = foundIndex;
		}
	}

	// Check if we need to rotate to next text
	const newOccurrence = occurrence + 1;
	if (newOccurrence >= ROTATION_THRESHOLD) {
		// Move to next text, reset occurrence
		const nextIndex = (currentIndex + 1) % rotations.length;
		return {
			text: rotations[nextIndex],
			occurrence: 0,
		};
	}

	// Keep current text, increment occurrence
	return {
		text: rotations[currentIndex],
		occurrence: newOccurrence,
	};
};

/**
 * Generate ghost text data based on experiment cohort and current state.
 * Calculates rotations, selects appropriate text, and returns the new GhostTextData.
 *
 * @returns GhostTextData object or undefined if feature is disabled
 */
export const generateGhostTextData = ({
	products,
	thirdPartyConfigs,
	primaryProduct,
	formatMessage,
	existingGhostTextData,
}: {
	products: ProductKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
	primaryProduct?: string;
	formatMessage: (message: any, values?: any) => string;
	existingGhostTextData?: GhostTextData;
}): GhostTextData => {
	// Get all text rotations
	const rotations = calculateGhostTextRotations({
		products,
		thirdPartyConfigs,
		primaryProduct,
		formatMessage,
	});

	// Select appropriate text based on current state
	const { text, occurrence } = selectGhostText(
		rotations,
		existingGhostTextData?.text,
		existingGhostTextData?.occurrence ?? 0,
	);

	return {
		text,
		occurrence,
		category: GhostTextExperimentCohort.THREE_P,
	};
};
